import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {QuestionOptions} from 'src/app/constants/question-options';
import {FormBuilder, FormControl} from '@angular/forms';
import {ProposalOverview} from '../../store/proposal/actions/proposal.actions';
import {BehaviorSubject, combineLatest, distinctUntilChanged, map, startWith, Subject, takeUntil} from 'rxjs';
import {ProposalFilterQuery} from '../../model/proposal-filter-query';
import {ProposalCheckboxFilter} from '../../model/proposal-checkbox-filter';
import isEqual from 'lodash-es/isEqual';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {LocalStorageItem} from '../../enum/local-storage-item';
import {selectFilterToDisplay} from '../../store/proposal/selectors/proposal.selectors';
import {FilterRole} from '../../enum/filter-role';
import {ProposalNumber} from '../../model/proposal-number';

@Component({
    selector: 'app-proposal-filter',
    templateUrl: './proposal-filter.component.html',
    styleUrls: ['./proposal-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ProposalFilterComponent implements OnInit, OnDestroy {
  QuestionOptions = QuestionOptions;
  protected destroy$ = new Subject<void>();

  @Input() set filterHidden(value) {
    if (value == null) {
      return;
    }
    this.filterHidden$.next(value);
  }
  @Input() tilesNumber: ProposalNumber = new ProposalNumber();

  filterHidden$ = new BehaviorSubject<boolean>(false);
  @Output() filterHiddenChange = new EventEmitter<boolean>();
  formGroup = this.fb.group({
    selectedRole: FilterRole.ALL,
    noCoverage: false,
    leadInsurerNeeded: false,
    followingInsurerNeeded: false,
    limitPrice: false,
    minimumAmountInsured: new FormControl<number>(null),
    maximumAmountInsured: new FormControl<number>(null),
  });

  selectedFilter: string = '';
  lineOfBusinessForm = this.fb.group({});
  roles$ = this.userStore.select(selectUserRoles);
  filterOptions$ = this.userStore.select(selectFilterToDisplay);
  Roles = Roles;
  showMoreFilters: boolean = false;
  numberOfLinesOfBusinessShowDefault = 10;
  numberOfLinesOfBusinessShow: number = this.numberOfLinesOfBusinessShowDefault;

  constructor(public proposalStore: Store<ProposalAppState>,
              public userStore: Store<UserAppState>,
              public fb: FormBuilder) {
  }

  ngOnInit(): void {
    this.getStoredFilters();
    this.setLineOfBusinessForm();
    combineLatest([
      this.lineOfBusinessForm.valueChanges.pipe(startWith(null)),
      this.formGroup.valueChanges.pipe(startWith(null))
    ])
      .pipe(
        map(() => this.getProposalCheckboxFilter()),
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$))
      .subscribe((proposalCheckboxFilter) => {
        this.proposalStore.dispatch(ProposalOverview.updateFilter(
          {
            filter: proposalCheckboxFilter
          }
        ));
        this.setStoredFilters(this.formGroup.value);
      });
    this.setupFilterHidden();

  }

  getProposalCheckboxFilter() {
    let proposalCheckboxFilter = new ProposalCheckboxFilter(this.formGroup.getRawValue());
    const noFilterSelected = Object.values(this.lineOfBusinessForm.getRawValue()).every(value => !value);
    if (noFilterSelected) {
      for (let control in this.lineOfBusinessForm.getRawValue()) {
        proposalCheckboxFilter.lineOfBusiness[control] = true;
      }
      proposalCheckboxFilter.includeOwn = true;
      localStorage.setItem(LocalStorageItem.LINE_OF_BUSINESS, '');
    } else {
      proposalCheckboxFilter.lineOfBusiness = this.lineOfBusinessForm.getRawValue();
      let lobs = "";
      for (let control in this.lineOfBusinessForm.getRawValue()) {
        if (proposalCheckboxFilter.lineOfBusiness[control] === true) {
          lobs = lobs + control + ",";
        }
      }
      localStorage.setItem(LocalStorageItem.LINE_OF_BUSINESS, lobs);
    }

    return new ProposalFilterQuery(proposalCheckboxFilter);
  }

  setupFilterHidden() {
    const filterHidden = localStorage.getItem(LocalStorageItem.FILTER_COLLAPSED);
    if (filterHidden == String(true)) {
      this.filterHidden$.next(true);
    } else if (filterHidden == String(false)) {
      this.filterHidden$.next(false);
    }
    this.filterHidden$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
        this.filterHiddenChange.emit(value);
        localStorage.setItem(LocalStorageItem.FILTER_COLLAPSED, String(value));
      }
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLineOfBusinessForm(): void {

    const lobs = localStorage.getItem(LocalStorageItem.LINE_OF_BUSINESS);
    for (let lineOfBusiness of QuestionOptions.lineOfBusiness) {
      this.lineOfBusinessForm.addControl(lineOfBusiness, new FormControl(false));
    }
    this.filterOptions$.pipe(takeUntil(this.destroy$)).subscribe(filters => {
        for (let control in this.lineOfBusinessForm.controls) {
          this.lineOfBusinessForm.removeControl(control);
        }
        for (let lineOfBusiness of filters) {
          if (lobs) {
            this.lineOfBusinessForm.addControl(lineOfBusiness.number, new FormControl(lobs.includes(lineOfBusiness.number)));
          } else {
            this.lineOfBusinessForm.addControl(lineOfBusiness.number, new FormControl(false));
          }

        }
      }
    );
  }

  calculateNumberOfLinesOfBusinessShown() {
    if (this.showMoreFilters) {
      this.numberOfLinesOfBusinessShow = 10;
      this.showMoreFilters = !this.showMoreFilters;
    } else {
      this.numberOfLinesOfBusinessShow = QuestionOptions.lineOfBusiness.length;
      this.showMoreFilters = !this.showMoreFilters;

    }
  }

  resetFilter() {
    this.formGroup.controls.noCoverage.setValue(false);
    this.formGroup.controls.leadInsurerNeeded.setValue(false);
    this.formGroup.controls.followingInsurerNeeded.setValue(false);
    this.formGroup.controls.limitPrice.setValue(false);
    this.formGroup.controls.maximumAmountInsured.setValue(null);
    this.formGroup.controls.minimumAmountInsured.setValue(null);
    this.formGroup.controls.selectedRole.setValue(FilterRole.ALL);
    this.lineOfBusinessForm.reset();
    this.setStoredFilters(this.formGroup.value);
  }

  hideFilter() {
    this.filterHidden$.next(true);
  }

  private getStoredFilters() {
    const storedNoCoverage = localStorage.getItem(LocalStorageItem.NO_COVERAGE);
    if (storedNoCoverage) {
      this.formGroup.controls.noCoverage.setValue(storedNoCoverage === "true");
    }
    const storedLeadInsurerNeeded = localStorage.getItem(LocalStorageItem.LEAD_INSURER_NEEDED);
    if (storedLeadInsurerNeeded) {
      this.formGroup.controls.leadInsurerNeeded.setValue(storedLeadInsurerNeeded === "true");
    }
    const storedFollowingInsurerNeeded = localStorage.getItem(LocalStorageItem.FOLLOWING_INSURER_NEEDED);
    if (storedFollowingInsurerNeeded) {
      this.formGroup.controls.followingInsurerNeeded.setValue(storedFollowingInsurerNeeded === "true");
    }
    const storedLimitPrice = localStorage.getItem(LocalStorageItem.LIMIT_PRICE);
    if (storedLimitPrice) {
      this.formGroup.controls.limitPrice.setValue(storedLimitPrice === "true");
    }
    const storedSelectedRole = localStorage.getItem(LocalStorageItem.SELECTED_ROLE);
    if (storedSelectedRole) {
      this.formGroup.controls.selectedRole.setValue(this.mapStringToFilterRole(storedSelectedRole));
    }

  }

  protected readonly FilterRole = FilterRole;

  mapStringToFilterRole(role: string) {
    switch (role) {
      case FilterRole.PRIVATE.toString():
        return FilterRole.PRIVATE;
      case FilterRole.HAS_OFFERS.toString():
        return FilterRole.HAS_OFFERS;
      case FilterRole.OFFER_SUBMITTED.toString():
        return FilterRole.OFFER_SUBMITTED;
      case FilterRole.REJECTED_PROPOSALS.toString():
        return FilterRole.REJECTED_PROPOSALS;
      case FilterRole.RETRACTED_PROPOSALS.toString():
        return FilterRole.RETRACTED_PROPOSALS;
      case FilterRole.RETRACTED_OFFERS.toString():
        return FilterRole.RETRACTED_OFFERS;
      case FilterRole.EXPIRED.toString():
        return FilterRole.EXPIRED;
      case FilterRole.MY_PROPOSALS.toString():
        return FilterRole.MY_PROPOSALS;
      case FilterRole.SUBSTITUTES.toString():
        return FilterRole.SUBSTITUTES;
      default:
        return FilterRole.ALL;
    }
  }

  private setStoredFilters(value: any) {
    localStorage.setItem(LocalStorageItem.NO_COVERAGE, value.noCoverage.toString());
    localStorage.setItem(LocalStorageItem.LEAD_INSURER_NEEDED, value.leadInsurerNeeded.toString());
    localStorage.setItem(LocalStorageItem.FOLLOWING_INSURER_NEEDED, value.followingInsurerNeeded.toString());
    localStorage.setItem(LocalStorageItem.LIMIT_PRICE, value.limitPrice.toString());
    localStorage.setItem(LocalStorageItem.SELECTED_ROLE, value.selectedRole.toString());
  }

  updateSelectedFilter(event: any) {
    this.selectedFilter = event.value;
  }
}
